/* Phase 37 — Checklist banner shown on the outlet dashboard when an outlet
 * was created from a template and still has ingredients without local prices.
 *
 * Dismissible via DB flag (re-appears on next session anyway because the
 * counter still has unpriced items). Auto-hides once everything is priced.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ChevronRight, X } from "lucide-react";
import api from "../api";
import { useOutlets } from "./OutletContext";

export default function TemplateChecklistBanner() {
  const { t } = useTranslation();
  const { activeOutlet } = useOutlets();
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    setHidden(false);
    setData(null);
    if (!activeOutlet?.id) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/outlets/${activeOutlet.id}/template-checklist`);
        if (alive) setData(data);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [activeOutlet?.id]);

  if (!data || hidden) return null;
  if (!data.template_applied) return null;
  if (data.unpriced_ingredients === 0) return null;
  if (data.template_dismissed) return null;

  const total = data.total_ingredients || 0;
  const priced = data.priced_ingredients || 0;
  const pct = total > 0 ? Math.round((priced / total) * 100) : 0;

  const dismiss = async () => {
    setHidden(true);
    try {
      await api.post(`/outlets/${activeOutlet.id}/dismiss-template-checklist`);
    } catch { /* non-blocking */ }
  };

  return (
    <div
      data-testid="template-checklist-banner"
      className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/[0.07] p-4 flex items-start gap-3"
    >
      <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-ink-primary">
          {t("template_checklist.title", { unpriced: data.unpriced_ingredients, total })}
        </div>
        <p className="text-xs text-ink-secondary mt-1 leading-relaxed">
          {t("template_checklist.description")}
        </p>
        <div className="mt-3 flex items-center gap-3">
          <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full bg-amber-400 transition-all duration-300"
              style={{ width: `${pct}%` }}
              data-testid="template-checklist-progress"
            />
          </div>
          <span className="text-[11px] text-ink-muted num shrink-0" data-testid="template-checklist-progress-text">
            {t("template_checklist.progress", { priced, total })}
          </span>
        </div>
        <Link
          to="/ingredients?unpriced=1"
          data-testid="template-checklist-cta"
          className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-amber-400 hover:text-amber-300"
        >
          {t("template_checklist.cta")} <ChevronRight size={12} />
        </Link>
      </div>
      <button
        type="button"
        onClick={dismiss}
        data-testid="template-checklist-dismiss"
        className="p-1 rounded hover:bg-white/10 shrink-0"
        title={t("template_checklist.dismiss")}
      >
        <X size={14} />
      </button>
    </div>
  );
}
